import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, take, switchMap, finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AuthTokenService } from './auth-token.service';
import { LogOut, SetValidationError } from 'src/app/features/admin/stores/admin.action';
import { IAppState } from 'src/app/shared/shared.interface';
import { IAuthResult } from './auth.interface';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private readonly REFRESH_TOKEN_URL = `${environment.baseUrls.serviceSecurity}/api/authenticate/refresh-token`;

  constructor(
    private authTokenService: AuthTokenService,
    private router: Router,
    private store: Store<IAppState>,
    private http: HttpClient
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        console.log('AuthInterceptor caught error:', error.status, request.url);
        if (error.status === 401 && !this.isRefreshTokenRequest(request)) {
          console.log('AuthInterceptor: Handling 401, calling refresh token...');
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Check if the request is a refresh token request to avoid infinite loop
   */
  private isRefreshTokenRequest(request: HttpRequest<any>): boolean {
    return request.url.includes('/api/authenticate/refresh-token');
  }

  /**
   * Handle 401 Unauthorized error by refreshing token and retrying request
   */
  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      console.log('AuthInterceptor: Calling refresh token endpoint:', this.REFRESH_TOKEN_URL);
      return this.refreshToken().pipe(
        switchMap((response: IAuthResult) => {
          console.log('AuthInterceptor: Refresh token SUCCESS, new token received');
          this.isRefreshing = false;

          // Store the new tokens
          this.authTokenService.setTokens(response);
          this.refreshTokenSubject.next(response.id_token);

          // Retry the original request with new token
          console.log('AuthInterceptor: Retrying original request:', request.url);
          return next.handle(this.addTokenToRequest(request, response.id_token));
        }),
        catchError((refreshError) => {
          console.log('AuthInterceptor: Refresh token FAILED:', refreshError);
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);

          // Refresh token failed - logout user
          this.logoutAndRedirect();
          return throwError(() => refreshError);
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    } else {
      // Another request is already refreshing the token
      // Wait for the refresh to complete and retry with new token
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          return next.handle(this.addTokenToRequest(request, token!));
        })
      );
    }
  }

  /**
   * Call the refresh token endpoint
   * The refresh token is sent via HttpOnly cookie automatically
   */
  private refreshToken(): Observable<IAuthResult> {
    return this.http.post<IAuthResult>(
      this.REFRESH_TOKEN_URL,
      {},
      { withCredentials: true } // Important: send cookies with request
    );
  }

  /**
   * Add the new access token to the request headers
   */
  private addTokenToRequest(
    request: HttpRequest<any>,
    token: string
  ): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Clear tokens and redirect to login page
   */
  private logoutAndRedirect(): void {
    // Dispatch logout actions
    this.store.dispatch(new SetValidationError(''));
    this.store.dispatch(new LogOut());

    // Clear all stored tokens and session data
    localStorage.clear();
    sessionStorage.clear();

    // Clear browser caches if available
    if (window.caches) {
      caches.keys().then((keyList) => {
        return Promise.all(
          keyList.map((key) => {
            return caches.delete(key);
          })
        );
      });
    }

    // Redirect to login page
    this.router.navigateByUrl('/login', { replaceUrl: true });
  }
}
